import { useState } from "react";
import { api, setToken, apiError } from "@/lib/api";
import { useAtlas } from "@/context/AtlasContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { TotalOpsMark } from "@/components/shared/Brand";

const policyOk = (pw) => pw.length >= 10 && /[A-Z]/.test(pw) && /[a-z]/.test(pw) && /\d/.test(pw);

export default function ForcePasswordChange() {
  const { user, logout } = useAtlas();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const newOk = policyOk(next);
  const matchOk = next.length > 0 && next === confirm;
  const canSubmit = current.length > 0 && newOk && matchOk && !saving;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: current, new_password: next });
      setToken(data.token);
      toast.success("Password updated");
      window.location.reload();
    } catch (err) {
      toast.error(apiError(err, "Could not update password"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="force-password-change">
      <form onSubmit={submit} className="w-full max-w-sm space-y-5 panel p-7 rise">
        <div className="flex items-center gap-2"><TotalOpsMark className="w-8 h-8" /></div>
        <div>
          <h1 className="font-heading text-2xl font-semibold">Set a new password</h1>
          <p className="text-sm text-slate-500 mt-1">
            {user?.email ? `Signed in as ${user.email}. ` : ""}Your account uses a temporary password. Choose a new one to continue.
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="fpc-current">Current (temporary) password</Label>
          <Input id="fpc-current" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" className="h-11 bg-white" data-testid="fpc-current" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="fpc-new">New password</Label>
          <Input id="fpc-new" type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" className="h-11 bg-white" data-testid="fpc-new" />
          <p className={`text-xs ${next && !newOk ? "text-amber-600" : "text-slate-400"}`}>At least 10 characters with an uppercase letter, a lowercase letter and a number.</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="fpc-confirm">Confirm new password</Label>
          <Input id="fpc-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" className="h-11 bg-white" data-testid="fpc-confirm" />
          {confirm && !matchOk && <p className="text-xs text-amber-600">Passwords do not match.</p>}
        </div>
        <Button type="submit" className="w-full h-11 rounded-full" disabled={!canSubmit} data-testid="fpc-submit">{saving ? "Updating…" : "Update password & continue"}</Button>
        <button type="button" onClick={logout} className="w-full text-xs text-slate-500 hover:text-slate-900" data-testid="fpc-signout">Sign out</button>
      </form>
    </div>
  );
}
